import { DocumentImageSize, DocumentTypeCode } from '@/publicSearch/constants';

type TAccessTokenResponse = {
  token: string;
  refreshToken: string;
};

export async function getAccessToken(
  azureAccessToken: string,
): Promise<TAccessTokenResponse> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${azureAccessToken}`,
  };

  let response = await fetch(
    `${process.env.NEXT_PUBLIC_AUTH_API_URL}/api/Access/GetAccessToken`,
    {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'include',
    },
  );

  return (await response!.json()) as Promise<TAccessTokenResponse>;
}

type TUserProfileResponse = {
  userProfileEntityId: string;
  userId: number;
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  preferredName?: string | null;
  displayName?: string | null;
  dateOfBirth?: string | null;
};

export async function getUserProfile(
  userId: string,
  accessToken: string,
): Promise<TUserProfileResponse> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    'ABRI-AUTH-CLIENT-ID': process.env.NEXT_PUBLIC_ABRI_AUTH_CLIENT_ID ?? '',
  };
  const params = new URLSearchParams({
    userId: userId,
  });

  let response = await fetch(
    `${process.env.NEXT_PUBLIC_AUTH_API_URL}/api/UserProfile?${params}`,
    {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'include',
    },
  ).catch(console.error);

  return (await response!.json()) as Promise<TUserProfileResponse>;
}

export async function getUserAvatar(
  userProfileEntityId: string,
  orgId: string,
): Promise<Blob> {
  const headers: Record<string, string> = {
    'X-Organisation-Id': orgId,
    'X-Society-Id': orgId,
  };
  const params = new URLSearchParams({
    entityId: userProfileEntityId,
    documentType: DocumentTypeCode.Avatar.toString(),
    size: DocumentImageSize.s,
  });

  let response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/api/Document/GetImageByEntityId?${params}`,
    {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'include',
    },
  ).catch(console.error);

  return await response!.blob();
}

export async function getEntityImage(
  entityGuid: string,
  documentType: DocumentTypeCode,
  size: DocumentImageSize,
  orgId: string,
): Promise<Blob> {
  const headers: Record<string, string> = {
    'X-Organisation-Id': orgId,
    'X-Society-Id': orgId,
  };
  const params = new URLSearchParams({
    entityId: entityGuid,
    documentType: documentType.toString(),
    size,
  });

  let response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/api/Document/GetImageByEntityId?${params}`,
    {
      method: 'GET',
      headers,
      mode: 'cors',
      credentials: 'include',
    },
  ).catch(console.error);

  return await response!.blob();
}

/**
 * Fetches a document image from a URL with X-Society-Id header.
 * Uses a same-origin API proxy to avoid CORS (proxy sends X-Society-Id server-side).
 */
export async function getDocumentImageByUrl(
  url: string,
  societyId: string,
  token?: string,
): Promise<Blob> {
  const proxyUrl = `/api/document?url=${encodeURIComponent(url)}&societyId=${encodeURIComponent(societyId)}`;
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(proxyUrl, {
    method: 'GET',
    credentials: 'include',
    headers,
  }).catch(console.error);

  if (!response?.ok) {
    throw new Error(`Failed to load image: ${response?.status}`);
  }
  return response.blob();
}
